import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  resolveStoreOnboardingState,
  type StoreAccessRole,
} from "@/lib/storeOnboarding";

type Role = "admin" | "manager" | "cashier";

type AuthCtx = {
  user: User | null;
  session: Session | null;
  roles: Role[];
  storeAccessRoles: StoreAccessRole[];
  hasStoreAccess: boolean;
  canCreateFirstStore: boolean;
  shouldAskAdminForStoreAccess: boolean;
  storeOnboardingStatus: ReturnType<typeof resolveStoreOnboardingState>["status"];
  isAdmin: boolean;
  isManager: boolean;
  loading: boolean;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  roles: [],
  storeAccessRoles: [],
  hasStoreAccess: false,
  canCreateFirstStore: false,
  shouldAskAdminForStoreAccess: false,
  storeOnboardingStatus: "needs_store_assignment",
  isAdmin: false,
  isManager: false,
  loading: true,
  refreshAuth: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [storeAccessRoles, setStoreAccessRoles] = useState<StoreAccessRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listener FIRST
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        // Defer DB call to avoid deadlock inside the callback
        setTimeout(() => loadAuthorization(s.user!.id), 0);
      } else {
        setRoles([]);
        setStoreAccessRoles([]);
      }
    });

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) await loadAuthorization(s.user.id);
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const loadAuthorization = async (uid: string) => {
    const [rolesRes, accessRes] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", uid),
      supabase.from("user_store_access").select("role").eq("user_id", uid),
    ]);
    setRoles(((rolesRes.data ?? []) as Array<{ role: string }>).map((r) => r.role as Role));
    setStoreAccessRoles(
      ((accessRes.data ?? []) as Array<{ role: string }>).map((r) => r.role as StoreAccessRole),
    );
  };

  const refreshAuth = async () => {
    if (user) await loadAuthorization(user.id);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const onboarding = resolveStoreOnboardingState({ user, roles, storeAccessRoles });
  const hasStoreSetupRole = onboarding.setupRoles.length > 0;
  const isAdmin = roles.includes("admin") || onboarding.setupRoles.includes("owner") || onboarding.setupRoles.includes("admin");
  const isManager = isAdmin || roles.includes("manager") || hasStoreSetupRole;

  return (
    <Ctx.Provider
      value={{
        user,
        session,
        roles,
        storeAccessRoles,
        hasStoreAccess: onboarding.hasStoreAccess,
        canCreateFirstStore: onboarding.canCreateFirstStore,
        shouldAskAdminForStoreAccess: onboarding.shouldAskAdminForStoreAccess,
        storeOnboardingStatus: onboarding.status,
        isAdmin,
        isManager,
        loading,
        refreshAuth,
        signOut,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
